import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EpcProjectWizard, { type WizardSubmitPayload, type WizardSubmitResult } from '../project/create/EpcProjectWizard';
import epcProjectService from '../../../services/epcProjectService';
import { fileService } from '../../../services/fileService';

function attachmentName(displayName: string, originalName: string, tagLabel: string): string {
  const ext = originalName.includes('.') ? '.' + originalName.split('.').pop() : '';
  const base = displayName.endsWith(ext) ? displayName : `${displayName}${ext}`;
  return `[${tagLabel}] ${base}`;
}

export default function EpcProjectCreatePage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  // Errors propagate to the wizard, which maps duplicate-code clashes to the
  // Basics tab and toasts everything else. Success renders the wizard's
  // post-create screen (Import MPP / Build team / Close).
  const handleSubmit = async (payload: WizardSubmitPayload): Promise<WizardSubmitResult> => {
    setSubmitting(true);
    try {
      const attachments: { fileName: string; fileKey: string }[] = [];
      for (const att of payload.newAttachments) {
        const { key } = await fileService.uploadFile(att.file);
        attachments.push({
          fileName: attachmentName(att.displayName, att.file.name, att.tagLabel),
          fileKey: key,
        });
      }

      const body: Record<string, unknown> = {
        name: payload.name,
        code: payload.code,
        projectTypeId: payload.projectTypeId,
        status: payload.status,
      };
      if (payload.customerId) body.customerId = payload.customerId;
      if (payload.projectCategoryId) body.projectCategoryId = payload.projectCategoryId;
      if (payload.locationIds.length) body.locationIds = payload.locationIds;
      if (payload.dcCapacityMw !== undefined) body.dcCapacityMw = payload.dcCapacityMw;
      if (payload.acCapacityMw !== undefined) body.acCapacityMw = payload.acCapacityMw;
      if (payload.commissioningDate) body.commissioningDate = payload.commissioningDate;
      if (payload.startDate) body.startDate = payload.startDate;
      if (payload.endDatePlanned) body.endDatePlanned = payload.endDatePlanned;
      if (payload.projectManager) body.projectManager = payload.projectManager;
      if (payload.remarks) body.remarks = payload.remarks;
      if (attachments.length) body.epcAttachments = attachments;

      const created = await epcProjectService.create(body);
      return { _id: created._id };
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <EpcProjectWizard
      mode="create"
      submitting={submitting}
      onSubmit={handleSubmit}
      onCancel={() => navigate('/epc/projects')}
    />
  );
}
